import { useState } from 'react'
import {
  Card, Row, Col, Form, DatePicker, Select, Button, Table, Tabs,
  Statistic, Tag, Typography, Space, Badge,
} from 'antd'
import {
  DownloadOutlined, SearchOutlined, ExperimentOutlined,
  CheckCircleOutlined, WarningOutlined, ClockCircleOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { reportsApi } from '@/features/reports/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const { RangePicker } = DatePicker

export default function QualityReportsPage() {
  const { plantId } = usePlantId()

  const [form] = Form.useForm()
  const [params, setParams] = useState({
    plant_id: plantId,
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().format('YYYY-MM-DD'),
    sample_type: undefined as string | undefined,
    result: undefined as string | undefined,
  })

  const { data: labTests, isLoading: labLoading } = useQuery({
    queryKey: ['quality-tests', params],
    queryFn: () => reportsApi.qualityTests(params),
    enabled: !!params.plant_id,
  })

  const { data: countSummary } = useQuery({
    queryKey: ['quality-summary-by-count', params],
    queryFn: () => reportsApi.qualitySummaryByCount(params),
    enabled: !!params.plant_id,
  })

  const { data: capaAging, isLoading: capaLoading } = useQuery({
    queryKey: ['capa-aging', plantId],
    queryFn: () => reportsApi.capaAging({ plant_id: plantId }),
    enabled: !!plantId,
  })

  const { data: capaSummary } = useQuery({
    queryKey: ['capa-summary', plantId],
    queryFn: () => reportsApi.capaSummary(plantId),
    enabled: !!plantId,
  })

  const { data: complaints } = useQuery({
    queryKey: ['complaints', params],
    queryFn: () => reportsApi.complaints({ plant_id: plantId, date_from: params.date_from, date_to: params.date_to }),
    enabled: !!plantId,
  })

  const handleSearch = () => {
    const vals = form.getFieldsValue()
    const [df, dt] = vals.dateRange || []
    setParams(prev => ({
      ...prev,
      date_from: df ? df.format('YYYY-MM-DD') : prev.date_from,
      date_to: dt ? dt.format('YYYY-MM-DD') : prev.date_to,
      sample_type: vals.sample_type,
      result: vals.result,
    }))
  }

  const labColumns = [
    { title: 'Test No', dataIndex: 'test_no', key: 'test_no', width: 110 },
    { title: 'Date', dataIndex: 'test_date', key: 'test_date', width: 110 },
    { title: 'Sample Type', dataIndex: 'sample_type', key: 'sample_type', width: 130 },
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne', width: 90, align: 'center' as const },
    { title: 'U%', dataIndex: 'u_pct', key: 'u_pct', width: 80, align: 'right' as const },
    { title: 'CV%', dataIndex: 'count_cv_pct', key: 'cv_pct', width: 80, align: 'right' as const },
    { title: 'Thin', dataIndex: 'thin_places', key: 'thin', width: 70, align: 'right' as const },
    { title: 'Thick', dataIndex: 'thick_places', key: 'thick', width: 70, align: 'right' as const },
    { title: 'Neps', dataIndex: 'neps', key: 'neps', width: 70, align: 'right' as const },
    { title: 'Strength (RKm)', dataIndex: 'strength_rkm', key: 'strength', width: 120, align: 'right' as const },
    { title: 'Micronaire', dataIndex: 'micronaire', key: 'mic', width: 90, align: 'right' as const },
    { title: 'SCI', dataIndex: 'sci', key: 'sci', width: 80, align: 'right' as const },
    {
      title: 'Result', dataIndex: 'result', key: 'result', width: 90,
      render: (v: string) => (
        <Tag color={v === 'PASS' ? 'green' : v === 'FAIL' ? 'red' : v === 'HOLD' ? 'orange' : 'blue'}>
          {v}
        </Tag>
      )
    },
    { title: 'Uster Grade', dataIndex: 'uster_grade', key: 'grade', width: 100, align: 'center' as const },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot', width: 100 },
  ]

  const countSummaryColumns = [
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne' },
    { title: 'Total Tests', dataIndex: 'total_tests', key: 'total', align: 'right' as const },
    { title: 'Pass', dataIndex: 'pass_count', key: 'pass', align: 'right' as const,
      render: (v: number) => <span style={{ color: '#389e0d', fontWeight: 600 }}>{v}</span> },
    { title: 'Fail', dataIndex: 'fail_count', key: 'fail', align: 'right' as const,
      render: (v: number) => <span style={{ color: '#cf1322' }}>{v}</span> },
    { title: 'Pass %', dataIndex: 'pass_pct', key: 'pass_pct', align: 'right' as const,
      render: (v: string) => {
        const n = parseFloat(v)
        return <span style={{ color: n >= 95 ? '#389e0d' : n >= 85 ? '#d46b08' : '#cf1322' }}>{v}%</span>
      }
    },
    { title: 'Avg Strength (RKm)', dataIndex: 'avg_strength_rkm', key: 'strength', align: 'right' as const },
    { title: 'Avg CV%', dataIndex: 'avg_count_cv_pct', key: 'cv', align: 'right' as const },
    { title: 'Avg U%', dataIndex: 'avg_u_pct', key: 'u', align: 'right' as const },
    { title: 'Avg Neps', dataIndex: 'avg_neps', key: 'neps', align: 'right' as const },
  ]

  const capaColumns = [
    { title: 'CAPA No', dataIndex: 'capa_no', key: 'capa_no', width: 120 },
    { title: 'Initiated', dataIndex: 'initiated_date', key: 'date', width: 110 },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 160, ellipsis: true },
    {
      title: 'Severity', dataIndex: 'severity', key: 'severity', width: 100,
      render: (v: string) => {
        const colors: Record<string, string> = { LOW: 'blue', MEDIUM: 'gold', HIGH: 'orange', CRITICAL: 'red' }
        return <Tag color={colors[v]}>{v}</Tag>
      }
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 140,
      render: (v: string) => {
        const colors: Record<string, string> = {
          OPEN: 'red', IN_PROGRESS: 'blue', OVERDUE: 'volcano',
          PENDING_VERIFICATION: 'gold', CLOSED: 'green', CANCELLED: 'default',
        }
        return <Tag color={colors[v] || 'default'}>{v.replace('_', ' ')}</Tag>
      }
    },
    { title: 'Problem', dataIndex: 'problem', key: 'problem', ellipsis: true },
    { title: 'Owner', dataIndex: 'action_owner', key: 'owner', width: 140 },
    { title: 'Target Date', dataIndex: 'target_date', key: 'target', width: 110 },
    { title: 'Days Open', dataIndex: 'days_open', key: 'days', width: 100, align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 30 ? '#cf1322' : v > 14 ? '#d46b08' : 'inherit' }}>{v}</span> },
  ]

  const complaintColumns = [
    { title: 'Complaint No', dataIndex: 'complaint_no', key: 'no', width: 130 },
    { title: 'Received', dataIndex: 'received_date', key: 'date', width: 110 },
    { title: 'Customer', dataIndex: 'customer', key: 'customer', width: 160, ellipsis: true },
    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count', width: 90 },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot', width: 100 },
    { title: 'Type', dataIndex: 'complaint_type', key: 'type', width: 130 },
    { title: 'Qty (kg)', dataIndex: 'qty_kg', key: 'qty', align: 'right' as const, width: 90 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v: string) => {
        const colors: Record<string, string> = {
          'OPEN': 'red', 'UNDER INVESTIGATION': 'orange', 'RESOLVED': 'green',
          'CLOSED': 'default', 'REJECTED': 'purple',
        }
        return <Tag color={colors[v] || 'default'}>{v}</Tag>
      }
    },
    { title: 'Disposition', dataIndex: 'disposition', key: 'disp', width: 100 },
    { title: 'Debit Note', dataIndex: 'debit_note', key: 'debit', align: 'right' as const, width: 100 },
    { title: 'CAPA No', dataIndex: 'capa_no', key: 'capa', width: 110 },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ExperimentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
        Quality Reports
      </Title>
      <Typography.Text type="secondary">Lab test results, count-wise quality, CAPA aging, customer complaints</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="dateRange" label="Period">
            <RangePicker
              defaultValue={[dayjs().startOf('month'), dayjs()]}
              format="DD-MMM-YYYY"
            />
          </Form.Item>
          <Form.Item name="sample_type" label="Sample Type">
            <Select allowClear style={{ width: 160 }} placeholder="All">
              {['FIBRE_HVI','CARD_SLIVER','DRAW_SLIVER','COMBED_SLIVER','ROVING','YARN_RING','YARN_CONE','SPLICE'].map(t => (
                <Select.Option key={t} value={t}>{t.replace('_', ' ')}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="result" label="Result">
            <Select allowClear style={{ width: 100 }} placeholder="All">
              {['PASS', 'FAIL', 'HOLD', 'RETEST'].map(r => (
                <Select.Option key={r} value={r}>{r}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Apply</Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.qualityTestsExportUrl(params)} download>
                Export Lab Tests
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.capaAgingExportUrl({ plant_id: plantId })} download>
                Export CAPA
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.complaintsExportUrl({ plant_id: plantId, date_from: params.date_from, date_to: params.date_to })} download>
                Export Complaints
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* CAPA KPI Bar */}
      {capaSummary && (
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={4}><Card><Statistic title="Total CAPAs" value={capaSummary.total} /></Card></Col>
          <Col span={4}><Card><Statistic title="Open" value={capaSummary.open} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} /></Card></Col>
          <Col span={4}><Card><Statistic title="In Progress" value={capaSummary.in_progress} valueStyle={{ color: '#1677ff' }} prefix={<ClockCircleOutlined />} /></Card></Col>
          <Col span={4}><Card><Statistic title="Overdue" value={capaSummary.overdue} valueStyle={{ color: '#d46b08' }} /></Card></Col>
          <Col span={4}><Card><Statistic title="Closed" value={capaSummary.closed} valueStyle={{ color: '#389e0d' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        </Row>
      )}

      <Tabs
        defaultActiveKey="lab"
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'lab',
            label: `Lab Tests (${Array.isArray(labTests) ? labTests.length : 0})`,
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.qualityTestsExportUrl(params)} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(labTests) ? labTests : []}
                  columns={labColumns}
                  rowKey="test_no"
                  loading={labLoading}
                  scroll={{ x: 1600 }}
                  size="small"
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'count-summary',
            label: 'Count-wise Summary',
            children: (
              <Card>
                <Table
                  dataSource={Array.isArray(countSummary) ? countSummary : []}
                  columns={countSummaryColumns}
                  rowKey="count_ne"
                  size="small"
                  pagination={false}
                />
              </Card>
            ),
          },
          {
            key: 'capa',
            label: (
              <span>
                CAPA Aging
                {capaSummary?.overdue > 0 && (
                  <Badge count={capaSummary.overdue} size="small" style={{ marginLeft: 8 }} />
                )}
              </span>
            ),
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />} href={reportsApi.capaAgingExportUrl({ plant_id: plantId })} download>
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(capaAging) ? capaAging : []}
                  columns={capaColumns}
                  rowKey="capa_no"
                  loading={capaLoading}
                  size="small"
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: 'complaints',
            label: `Complaints (${Array.isArray(complaints) ? complaints.length : 0})`,
            children: (
              <Card
                extra={
                  <Button size="small" icon={<DownloadOutlined />}
                    href={reportsApi.complaintsExportUrl({ plant_id: plantId, date_from: params.date_from, date_to: params.date_to })}
                    download
                  >
                    Export Excel
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(complaints) ? complaints : []}
                  columns={complaintColumns}
                  rowKey="complaint_no"
                  size="small"
                  scroll={{ x: 1200 }}
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  )
}
